import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IUser } from '../interfaces/user';
import { IPageRoles } from '../interfaces/permission';
import { LoggerService } from './logger.service';
import { TaggedLogger } from './taggedLogger';
import { UserService } from './user.service';
import { RoutesService } from './routes.service';
import { ISecurityHandler } from '../handlers/security/securityHandler';
import { RolesSecurityHandler } from '../handlers/security/rolesSecurityHandler';

@Injectable({ providedIn: 'root' })
export class SecurityService {
  private _loggedUser: IUser;
  private _pagesRoles: IPageRoles[];

  get pageRoles(): IPageRoles[] {
    return this._pagesRoles;
  }

  private logger: TaggedLogger;

  private initPromise: Promise<void>;

  constructor(
    private userService: UserService,
    private routesService: RoutesService,
    private router: Router,
    private loggerService: LoggerService,
  ) {
    this.logger = loggerService.taggedLogger('SecurityService');
  }

  /**
   * Called on first request to this service to ensure the logged in user is available
   * note: previously was an APP_INITIALIZER which causes some build stability issues
   */
  private init() {
    if (this.initPromise) return this.initPromise;

    this.logger.trace('init');
    const userPromise = this.userService.getLoggedUser().then((user) => {
      this._loggedUser = user;
    });

    const rolePromise = this.routesService.getPageRoles().then((roles) => {
      this._pagesRoles = roles;
    });

    this.initPromise = Promise.all([userPromise, rolePromise])
      .then(() => {
        this.logger.trace('Initialized');
        this.initPromise = null;
      })
      .catch((error) => {
        this.logger.error('Could not initialize, user is likely not logged in', error);
        this.initPromise = null;
        throw error;
      });

    return this.initPromise;
  }

  pageIsAvailable(path: string[], customHandler?: ISecurityHandler): Promise<boolean> {
    if (!this._pagesRoles || !this._loggedUser) {
      return this.init().then(() => this.isAvailable(path, customHandler));
    } else {
      return Promise.resolve(this.isAvailable(path, customHandler));
    }
  }

  private isAvailable(path: string[], customHandler?: ISecurityHandler) {
    this.logger.trace('isAvailable()', path);

    let handler = customHandler || new RolesSecurityHandler(this.loggerService);
    let page = this.getPagesRoles(path);

    if (!page) return false;
    return handler.handle(this._loggedUser, page);
  }

  private getPagesRoles(path: string[]) {
    this.logger.trace('getPageRoles()', path);

    return this._pagesRoles.find((p) => path.indexOf(p.path) !== -1);
  }

  getAvailableRoutesForDebug(): string {
    try {
      let result = ['nothing'];

      if (this.pageRoles && typeof this.pageRoles.map === 'function') {
        const available = this.pageRoles.map((role) => role.path);
        if (available && typeof available.sort === 'function') {
          result = available.sort();
        }
      }

      return JSON.stringify(result, null, 2);
    } catch (e) {
      return 'nothing';
    }
  }
}
